import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Trash2, Plus, HelpCircle } from "lucide-react";
import {
  CoverageAmountType,
  EarningsBase,
} from "@shared/schema";

const COVERAGE_AMOUNT_TYPE_OPTIONS = [
  { value: CoverageAmountType.FLAT_AMOUNT, label: "Flat Amount", description: "Fixed dollar amount (e.g., $100,000)" },
  { value: CoverageAmountType.EARNINGS_MULTIPLE, label: "Multiple of Earnings", description: "Multiple of salary (e.g., 2x, 3x)" },
  { value: CoverageAmountType.EARNINGS_PERCENTAGE, label: "Percentage of Earnings", description: "Percentage of salary (e.g., 60%)" },
  { value: CoverageAmountType.INCREMENTS, label: "Increments", description: "Employee selects in fixed increments (e.g., $10K)" },
  { value: CoverageAmountType.CUSTOM_AMOUNTS, label: "Custom Amounts", description: "Predefined amount options to choose from" },
];

const EARNINGS_BASE_OPTIONS = [
  { value: EarningsBase.BASE_SALARY, label: "Base Salary Only" },
  { value: EarningsBase.BASE_PLUS_BONUS, label: "Base Salary + Bonus" },
  { value: EarningsBase.BASE_PLUS_COMMISSION, label: "Base Salary + Commission" },
  { value: EarningsBase.TOTAL_COMPENSATION, label: "Total Compensation" },
  { value: EarningsBase.W2_EARNINGS, label: "W2 Earnings" },
];

function FieldLabel({ htmlFor, required, tooltip, children }: { htmlFor?: string; required?: boolean; tooltip: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 mb-1">
      <Label htmlFor={htmlFor} className="mb-0">
        {children}{required && " *"}
      </Label>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-sm">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

interface AmountsTabProps {
  formData: Record<string, any>;
  updateField: (field: string, value: any) => void;
  planType: string;
}

export function AmountsTab({ formData, updateField, planType }: AmountsTabProps) {
  const isDisability = ["std", "ltd"].includes(planType?.toLowerCase());
  const isEarningsBased =
    formData.coverageAmountType === CoverageAmountType.EARNINGS_MULTIPLE ||
    formData.coverageAmountType === CoverageAmountType.EARNINGS_PERCENTAGE;

  // Parse custom amounts from JSON string
  const customAmounts: string[] = (() => {
    try {
      return formData.customAmounts ? JSON.parse(formData.customAmounts) : [];
    } catch {
      return [];
    }
  })();

  const addCustomAmount = () => {
    const updated = [...customAmounts, ""];
    updateField("customAmounts", JSON.stringify(updated));
  };

  const removeCustomAmount = (index: number) => {
    const updated = customAmounts.filter((_, i) => i !== index);
    updateField("customAmounts", JSON.stringify(updated));
  };

  const updateCustomAmount = (index: number, value: string) => {
    const updated = customAmounts.map((v, i) => (i === index ? value : v));
    updateField("customAmounts", JSON.stringify(updated));
  };

  return (
    <div className="space-y-4">
      {/* Coverage Amount Type */}
      <div>
        <FieldLabel htmlFor="coverageAmountType" required tooltip="How the benefit amount is determined for each employee — flat dollar amount, based on earnings, employee-selected increments, or predefined options.">Coverage Amount Type</FieldLabel>
        <Select
          value={formData.coverageAmountType}
          onValueChange={(value) => updateField("coverageAmountType", value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COVERAGE_AMOUNT_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <div>
                  <div className="font-medium">{option.label}</div>
                  <div className="text-xs text-muted-foreground">{option.description}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Flat Amount */}
      {formData.coverageAmountType === CoverageAmountType.FLAT_AMOUNT && (
        <div>
          <FieldLabel htmlFor="flatCoverageAmount" required tooltip="The fixed dollar amount of coverage every eligible employee receives, regardless of salary.">Flat Coverage Amount</FieldLabel>
          <Input
            id="flatCoverageAmount"
            type="number"
            step="1000"
            value={formData.flatCoverageAmount || ""}
            onChange={(e) => updateField("flatCoverageAmount", e.target.value)}
            placeholder="100000"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Fixed coverage amount for all eligible employees
          </p>
        </div>
      )}

      {/* Earnings Multiple */}
      {formData.coverageAmountType === CoverageAmountType.EARNINGS_MULTIPLE && (
        <div className="space-y-4">
          <div>
            <FieldLabel htmlFor="earningsMultiple" required tooltip="Coverage equals this number times the employee's earnings. Example: 2x with a $60K salary = $120K coverage.">Earnings Multiple</FieldLabel>
            <Input
              id="earningsMultiple"
              type="number"
              step="0.5"
              value={formData.earningsMultiple || ""}
              onChange={(e) => updateField("earningsMultiple", e.target.value)}
              placeholder="2.0"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Multiply earnings by this amount (e.g., 2x salary = 2.0)
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel htmlFor="additionalFlatAmount" tooltip="A fixed dollar amount added on top of the earnings-based calculation. Example: 2x salary + $10,000.">Additional Flat Amount</FieldLabel>
              <Input
                id="additionalFlatAmount"
                type="number"
                step="1000"
                value={formData.additionalFlatAmount || ""}
                onChange={(e) => updateField("additionalFlatAmount", e.target.value)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Extra flat amount on top of earnings multiple
              </p>
            </div>
            <div>
              <FieldLabel htmlFor="maximumTimesEarnings" tooltip="Cap on the earnings multiplier. Limits how high coverage can go regardless of the base multiple.">Maximum Times Earnings</FieldLabel>
              <Input
                id="maximumTimesEarnings"
                type="number"
                step="0.5"
                value={formData.maximumTimesEarnings || ""}
                onChange={(e) => updateField("maximumTimesEarnings", e.target.value)}
                placeholder="8.0"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Ceiling multiplier (e.g., 8x max)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Earnings Percentage */}
      {formData.coverageAmountType === CoverageAmountType.EARNINGS_PERCENTAGE && (
        <div>
          <FieldLabel htmlFor="earningsPercentage" required tooltip="Coverage equals this percentage of the employee's earnings. Example: 60% of a $5,000/mo salary = $3,000/mo benefit.">Earnings Percentage</FieldLabel>
          <Input
            id="earningsPercentage"
            type="number"
            step="1"
            value={formData.earningsPercentage || ""}
            onChange={(e) => updateField("earningsPercentage", e.target.value)}
            placeholder="60"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Percentage of earnings (e.g., 60% = enter 60)
          </p>
        </div>
      )}

      {/* Increments */}
      {formData.coverageAmountType === CoverageAmountType.INCREMENTS && (
        <div>
          <p className="text-sm text-muted-foreground mb-2">
            Employee selects coverage in fixed increments between min and max below.
          </p>
        </div>
      )}

      {/* Custom Amounts */}
      {formData.coverageAmountType === CoverageAmountType.CUSTOM_AMOUNTS && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Custom Amount Options</Label>
            <Button type="button" variant="outline" size="sm" onClick={addCustomAmount}>
              <Plus className="h-4 w-4 mr-1" /> Add Amount
            </Button>
          </div>
          {customAmounts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2">
              No custom amounts configured. Click "Add Amount" to add options.
            </p>
          ) : (
            <div className="space-y-2">
              {customAmounts.map((amount, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="1000"
                    value={amount}
                    onChange={(e) => updateCustomAmount(index, e.target.value)}
                    placeholder="50000"
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeCustomAmount(index)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Earnings Base (for earnings-based types) */}
      {isEarningsBased && (
        <>
          <Separator />
          <div>
            <FieldLabel htmlFor="earningsBase" required tooltip="Which compensation components are included when calculating earnings-based coverage.">Earnings Base</FieldLabel>
            <Select
              value={formData.earningsBase}
              onValueChange={(value) => updateField("earningsBase", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select earnings base" />
              </SelectTrigger>
              <SelectContent>
                {EARNINGS_BASE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      <Separator />

      {/* Min/Max/Increment */}
      <div className={`grid ${isDisability ? "grid-cols-2" : "grid-cols-3"} gap-4`}>
        <div>
          <FieldLabel htmlFor="minimumCoverage" tooltip="The lowest coverage amount an employee can have. Applies across all coverage types.">Minimum Coverage</FieldLabel>
          <Input
            id="minimumCoverage"
            type="number"
            step="1000"
            value={formData.minimumCoverage || ""}
            onChange={(e) => updateField("minimumCoverage", e.target.value)}
            placeholder="10000"
          />
        </div>
        {!isDisability && (
          <div>
            <FieldLabel htmlFor="maximumCoverage" tooltip="The highest coverage amount allowed, regardless of salary or multiplier. Also known as the benefit cap.">Maximum Coverage</FieldLabel>
            <Input
              id="maximumCoverage"
              type="number"
              step="1000"
              value={formData.maximumCoverage || ""}
              onChange={(e) => updateField("maximumCoverage", e.target.value)}
              placeholder="500000"
            />
          </div>
        )}
        <div>
          <FieldLabel htmlFor="coverageIncrement" tooltip="Employee must select coverage in multiples of this amount. Example: $10,000 increments means $10K, $20K, $30K, etc.">Coverage Increment</FieldLabel>
          <Input
            id="coverageIncrement"
            type="number"
            step="1000"
            value={formData.coverageIncrement || ""}
            onChange={(e) => updateField("coverageIncrement", e.target.value)}
            placeholder="10000"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Must be in multiples of this amount
          </p>
        </div>
      </div>
    </div>
  );
}
